import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { FormProvider, useForm } from "react-hook-form";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import SelectOption from "components/common/Select/Select";
import AddressAutoComplete from "components/common/GoogleMap/AddressAutoComplete";
import { toast } from "react-toastify";
import { classNames } from "Utils/utils";
import { STATES_LIST } from "Constants/constant";

/** Stable reference — inline object would re-run AddressAutoComplete's effect every render. */
const ADDRESS_CUSTOM_NAMES = { state: "state" };

/** API may return shift_location as a JSON string or an already-parsed object. */
const parseShiftLocation = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
};

/** Convert state string/code to STATES_LIST value (full name) for SelectOption. */
const getStateValueFromString = (stateString) => {
  if (stateString == null || stateString === "") return null;
  const stateStr =
    typeof stateString === "string" ? stateString : String(stateString);
  const statesListMatch = STATES_LIST.find(
    (s) =>
      s.value?.toLowerCase() === stateStr.toLowerCase() ||
      s.label?.toLowerCase() === stateStr.toLowerCase()
  );

  return statesListMatch?.value || null;
};

const LocationModal = ({
  openModal,
  handleClose,
  isUserGracePeriodOver,
  setShiftLocation,
  getCatalogServicesSubCategoryRequest: stateCatServicesRequestCall,
  catalogSubCategoryStatesLoading,
  stateOptions,
  selectedParticipant,
  shiftData = null,
  shiftLocation,
  /** Above WorkerShift fullscreen modal (z-[99999]) and advance dropdowns. */
  overlayZIndex = 100000,
}) => {
  const methods = useForm({
    defaultValues: {
      street_address: "",
      suburb: "",
      state: "",
      postal_code: "",
    },
  });
  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors },
    setValue,
    reset,
  } = methods;
  const watchFormData = watch();
  const didSeedFormRef = useRef(false);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Seed address fields once when the modal opens. Re-seeding on every
  // selectedParticipant / shiftLocation identity change fights Google
  // autocomplete (state dropdown flicker after clear + re-select).
  useEffect(() => {
    if (!openModal) {
      didSeedFormRef.current = false;
      return;
    }

    if (didSeedFormRef.current) {
      return;
    }

    if (!STATES_LIST || STATES_LIST.length === 0) {
      return;
    }

    didSeedFormRef.current = true;
    const shiftDataLocation = parseShiftLocation(shiftData?.shift_location);

    if (shiftLocation?.street) {
      setValue("suburb", shiftLocation?.suburb || "");
      setValue("postal_code", shiftLocation?.postcode || "");
      setValue("street_address", shiftLocation?.street || "");
      const stateValue = getStateValueFromString(
        shiftLocation?.state || shiftLocation?.state_name
      );
      setValue("state", stateValue || "");
    } else if (shiftData && shiftDataLocation) {
      setValue("suburb", shiftDataLocation?.suburb || "");
      setValue("postal_code", shiftDataLocation?.postcode || "");
      setValue("street_address", shiftDataLocation?.street || "");
      const stateValue = getStateValueFromString(shiftDataLocation?.state);
      setValue("state", stateValue || "");
    } else if (selectedParticipant) {
      let participantFromSelection = selectedParticipant;
      if (
        Array.isArray(selectedParticipant) &&
        selectedParticipant.length > 0
      ) {
        participantFromSelection =
          selectedParticipant.find(
            (item) => item?.suburb && item?.post_code && item?.state_id
          ) || selectedParticipant[0];
      }
      setValue("suburb", participantFromSelection?.suburb || "");
      setValue("postal_code", participantFromSelection?.post_code || "");
      setValue("street_address", participantFromSelection?.street_no || "");
      const stateValue = getStateValueFromString(
        participantFromSelection?.state
      );
      setValue("state", stateValue || "");
    }
  }, [openModal, selectedParticipant, setValue, shiftData, shiftLocation]);

  const selectedStateOption = useMemo(() => {
    const formState = watchFormData?.state;
    const formStateStr =
      formState != null ? String(formState).toLowerCase() : "";
    if (!formStateStr) {
      return null;
    }
    return (
      STATES_LIST.find(
        (x) =>
          x.value?.toLowerCase() === formStateStr ||
          x.label?.toLowerCase() === formStateStr
      ) || null
    );
  }, [watchFormData?.state]);

  const modalFooter = () => (
    <CustomButton
      isDisabled={isUserGracePeriodOver}
      label='Add Location'
      variant='primary'
      clickHandler={() => {
        if (isUserGracePeriodOver) {
          return;
        }
        const buttonRef = document.getElementById("locationButton");
        if (buttonRef) {
          buttonRef.click();
        }
      }}
    />
  );

  const onSubmit = async (data) => {
    if (isUserGracePeriodOver) {
      return;
    }

    // Build full address string
    const address = `${data.street_address}, ${data.suburb}, ${data.state} ${data.postal_code}`;

    let lat = null;
    let lng = null;

    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      // Wrap geocode in a promise for async/await
      const getLatLng = (address) => {
        return new Promise((resolve) => {
          geocoder.geocode({ address }, (results, status) => {
            if (status === "OK" && results && results[0]) {
              const location = results[0].geometry.location;
              resolve({ lat: location.lat(), lng: location.lng() });
            } else {
              resolve({ lat: null, lng: null });
            }
          });
        });
      };
      const coords = await getLatLng(address);
      lat = coords.lat;
      lng = coords.lng;
    }

    // Find the state label (short code) from STATES_LIST
    const stateLabel = STATES_LIST.find(
      (x) => x.value?.toLowerCase() === (data.state != null ? String(data.state) : "").toLowerCase()
    )?.label || data.state;

    setShiftLocation({
      street: data.street_address,
      suburb: data.suburb,
      state: data.state, // Full state name (e.g., "Victoria")
      postcode: data.postal_code,
      state_name: stateLabel, // Short code (e.g., "VIC")
      lat,
      lng,
    });

    if (lat !== null && lng !== null) {
      toast.success("Location added successfully!");
      handleClose(); // Do not close modal automatically
    } else {
      toast.error("Address not found.Please check and try again");
    }
  };

  if (!openModal) {
    return null;
  }

  const modal = (
    <div>
      <ModalSkeleton
        title='Choose Location'
        cssClass='w-[50%]'
        isOpen={openModal}
        closeModal={handleClose}
        modalFooter={modalFooter()}
        overlayZIndex={overlayZIndex}
      >
        <div>
          <FormProvider {...methods}>
            <form className='px-4' onSubmit={handleSubmit(onSubmit)}>
              <div className='col-span-2'>
                <AddressAutoComplete
                  customNames={ADDRESS_CUSTOM_NAMES}
                  google={window?.google}
                  errors={errors}
                >
                  <div className='grid grid-cols-2 gap-y-4 gap-x-5'>
                    <div className='col-span-1'>
                      <label
                        htmlFor=''
                        className='block text-sm font-medium text-gray-700'
                      >
                        Street Address
                        <span className='text-red-500'>*</span>
                      </label>
                      <div className='relative mt-1'>
                        <input
                          type='text'
                          name='street_address'
                          id='address-input'
                          className={classNames(
                            "did-floating-input",
                            errors.street_address && "errorBorder"
                          )}
                          {...register("street_address", {
                            required: true,
                            maxLength: 254,
                          })}
                        />
                        {errors.street_address && (
                          <span className='text-red-500 text-sm'>
                            Field is required!
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor=''
                        className='block text-sm font-medium text-gray-700'
                      >
                        Suburb<span className='text-red-500'>*</span>
                      </label>
                      <div className='relative mt-1'>
                        <input
                          type='text'
                          name='suburb'
                          id='suburb'
                          className={classNames(
                            "did-floating-input",
                            errors.suburb && "errorBorder"
                          )}
                          {...register("suburb", {
                            required: true,
                            maxLength: 254,
                          })}
                        />
                        {errors.suburb && (
                          <span className='text-red-500 text-sm'>
                            Field is required!
                          </span>
                        )}
                      </div>
                    </div>
                    <div className='col-span-1'>
                      <div className=''>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                          State<span className='text-red-500'>*</span>
                        </label>
                        <SelectOption
                          altName='google_state_id'
                          useMenuPortal={true}
                          // Above LocationModal overlay (default 100000); Select default portal is 10010.
                          menuPortalZIndex={(overlayZIndex ?? 100000) + 1}
                          isDisabled={
                            catalogSubCategoryStatesLoading &&
                            (!stateOptions || stateOptions.length === 0)
                          }
                          loading={catalogSubCategoryStatesLoading}
                          cssClass='truncate'
                          isRequired={true}
                          register={register}
                          setValue={setValue}
                          name='state'
                          clearErrors={clearErrors}
                          isSearch={false}
                          data={STATES_LIST}
                          selectedOption={selectedStateOption}
                          persistSelectedOption={true}
                          preserveSelectionWhenOptionMissing={true}
                          errorMessage={
                            errors?.state?.type === "required" ? (
                              <div className='text-sm text-red-500'>
                                Field is required!!
                              </div>
                            ) : null
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor=''
                        className='block text-sm font-medium text-gray-700'
                      >
                        Postcode<span className='text-red-500'>*</span>
                      </label>
                      <div className='relative mt-1'>
                        <input
                          type='number'
                          name='postal_code'
                          id='postal_code'
                          minLength={4}
                          maxLength={4}
                          className={classNames(
                            "did-floating-input",
                            errors.postal_code && "errorBorder"
                          )}
                          {...register("postal_code", {
                            required: true,
                            maxLength: 4,
                            minLength: 4,
                            onChange: (e) => {
                              e.target.value = e.target.value
                                .toString()
                                .slice(0, 4);
                            },
                          })}
                        />
                        {errors?.postal_code?.type === "maxLength" ? (
                          <span className='text-red-500 text-sm'>
                            Postcode must be 4 digits
                          </span>
                        ) : errors?.postal_code?.type === "minLength" ? (
                          <span className='text-red-500 text-sm'>
                            Postcode must be 4 digits
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </AddressAutoComplete>
              </div>
              <button className='sr-only hidden' id='locationButton' />
            </form>
          </FormProvider>
        </div>
      </ModalSkeleton>
    </div>
  );

  if (typeof document !== "undefined") {
    return createPortal(modal, document.body);
  }

  return modal;
};

export default LocationModal;
